import { Component, Input, OnDestroy, inject } from '@angular/core';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { sortBy } from 'lodash';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { LocalStorageService } from 'ngx-webstorage';
import { AuditService } from 'src/app/core/service/audit.service';
import { AuditProgramListService } from 'src/app/core/service/grid-data/grid-data-audit-program.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from 'src/app/shared/shared.module';
import { ProgramAuditDetailsComponent } from '../program-audit-details/program-audit-details.component';

@Component({
  selector: 'app-program-audit-list',
  imports: [CommonModule, TranslateModule, SharedModule, ProgramAuditDetailsComponent],
  templateUrl: './program-audit-list.component.html',
  styleUrls: ['./program-audit-list.component.scss']
})
export class ProgramAuditListComponent implements OnDestroy
{
  public pageSizes = [5, 10, 25, 50, 100];
  public currentProgramId: number;
  public currentEnableAdvanced: boolean;
  public revisionTypes: Array<{ text: string; value: number }>;
  public auditTypes: Array<{ text: string; value: number }>;
  public changesOnly = true;

  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'revisionId', dir: 'desc' }
    ]
  };

  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;

  private readonly appConfigService = inject(AppConfigService);
  private readonly auditProgramListService = inject(AuditProgramListService);
  private readonly messengerService = inject(MessengerService);
  private readonly storageService = inject(LocalStorageService);
  private readonly auditService = inject(AuditService);

  private listSubscription?: Subscription;

  constructor()
  {
    this.changesOnly = this.initializeChangesOnly();
  }

  public get loading(): boolean
  {
    return this.auditProgramListService.loading;
  }

  @Input() public set programId(programId: number)
  {
    if (programId !== undefined)
    {
      this.currentProgramId = programId;
      this.state.skip = 0;
      this.revisionTypes = sortBy(this.auditService.revisionTypes, ['text']);
      this.auditTypes = sortBy(this.auditProgramListService.auditTypes, ['text']);
      this.loadAuditProgram();
    }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.auditProgramListService.queryByState(state);
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  public onChangesOnlyChange(newValue: boolean): void
  {
    this.changesOnly = newValue;
    this.storageService.store('changes-only-audit-programs', this.changesOnly);
    this.messengerService.sendMessage(new MessageSubject('refreshAuditProgramDetails'));
  }

  private loadAuditProgram(): void
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.state.filter.filters.push({ field: 'programId', operator: 'eq', value: this.currentProgramId });
    this.listSubscription = this.auditProgramListService.subscribe({
      next: (value: any) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.timestampUTC = new Date(d.timestampUTC);
            d.revisionTypeText = this.auditService.getRevisionTypeText(d.revisionType);
            d.auditTypeText = this.auditProgramListService.getAuditTypeText(d.auditType);
          });
          this.gridView = {
            data: (value) ? this.gridData = value.data : this.gridData = [],
            total: (value) ? this.total = value.total : this.total = 0
          };
        }
      }
    });
    this.auditProgramListService.queryByState(this.state);
  }

  private initializeChangesOnly(): boolean
  {
    const storedValue = this.storageService.retrieve('changes-only-audit-programs');

    if (storedValue === null || storedValue === undefined)
    {
      this.storageService.store('changes-only-audit-programs', true);
      return true;
    }
    return storedValue;
  }
}
